<template>
  <div class="analysis-running-wall fixed inset-0 bg-gray-900 z-[9999] flex items-center justify-center">
    <div class="max-w-2xl w-full mx-4 text-center text-white">
      <!-- Logo -->
      <div class="mb-8">
        <Logo variant="white" size="lg" />
      </div>

      <!-- COMPLETE STATE -->
      <template v-if="isComplete">
        <!-- Success Animation -->
        <div class="loading-container mb-8">
          <div class="relative w-32 h-32 mx-auto">
            <div class="absolute inset-0 border-4 border-green-500 rounded-full"></div>
            <div class="absolute inset-4 bg-green-500/20 rounded-full flex items-center justify-center">
              <i class="pi pi-check text-6xl text-green-500"></i>
            </div>
          </div>
        </div>

        <!-- Title -->
        <h2 class="text-3xl font-bold mb-4">
          {{ t('analysis_complete_title', 'Analysis Complete!') }}
        </h2>

        <!-- Description -->
        <p class="text-xl text-gray-300 mb-8">
          {{ t('analysis_complete_description', 'Your analysis has finished running. Refresh the page to see your updated results.') }}
        </p>

        <!-- Refresh Button -->
        <button
          @click="handleRefresh"
          class="px-8 py-3 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 mx-auto"
        >
          <i class="pi pi-refresh"></i>
          {{ t('analysis_refresh_page', 'Refresh Page') }}
        </button>

        <!-- Info Notice -->
        <div class="mt-8 text-gray-400 text-sm">
          <i class="pi pi-info-circle mr-2"></i>
          {{ t('analysis_complete_info', 'Your data has been updated with the latest analysis results.') }}
        </div>
      </template>

      <!-- RUNNING STATE -->
      <template v-else>
        <!-- Loading Animation -->
        <div class="loading-container mb-8">
          <div class="relative w-32 h-32 mx-auto">
            <!-- Outer ring -->
            <div class="absolute inset-0 border-4 border-gray-700 rounded-full"></div>
            <!-- Animated ring -->
            <div class="absolute inset-0 border-4 border-t-green-500 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
            <!-- Inner content -->
            <div class="absolute inset-4 bg-gray-800 rounded-full flex items-center justify-center">
              <i class="pi pi-chart-line text-4xl text-green-500"></i>
            </div>
          </div>
        </div>

        <!-- Title -->
        <h2 class="text-3xl font-bold mb-4">
          {{ t('analysis_running_title', 'Analyzing Your Business') }}
        </h2>

        <!-- Description -->
        <p class="text-xl text-gray-300 mb-8">
          {{ t('analysis_running_description', "We're gathering metrics based on all your configured prompts and analyzing your website for the latest information.") }}
        </p>

        <!-- Progress Steps -->
        <div class="progress-steps mb-8">
          <div class="flex items-center justify-center gap-4">
            <div
              v-for="(step, index) in steps"
              :key="index"
              class="flex items-center gap-2"
            >
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center font-semibold transition-all duration-500"
                :class="{
                  'bg-green-500 text-white': currentStep > index,
                  'bg-green-500 text-white animate-pulse': currentStep === index,
                  'bg-gray-700 text-gray-400': currentStep < index
                }"
              >
                <i v-if="currentStep > index" class="pi pi-check"></i>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <span
                class="text-sm hidden sm:block"
                :class="{
                  'text-white': currentStep >= index,
                  'text-gray-500': currentStep < index
                }"
              >
                {{ step }}
              </span>
              <div
                v-if="index < steps.length - 1"
                class="w-8 h-0.5 transition-all duration-500"
                :class="{
                  'bg-green-500': currentStep > index,
                  'bg-gray-700': currentStep <= index
                }"
              ></div>
            </div>
          </div>
        </div>

        <!-- Email Notification Notice -->
        <div class="email-notice bg-gray-800/50 rounded-lg p-4 inline-block">
          <div class="flex items-center gap-3 text-gray-400">
            <i class="pi pi-envelope"></i>
            <span class="text-sm">
              {{ t('analysis_email_notice', "We'll send you an email when your analysis is complete") }}
            </span>
          </div>
        </div>

        <!-- Refresh Warning -->
        <div class="mt-8 text-gray-500 text-sm">
          <i class="pi pi-info-circle mr-2"></i>
          {{ t('analysis_refresh_warning', "Don't worry if you refresh - we'll continue the analysis in the background") }}
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Logo from '../Logo.vue';

const { t } = useI18n();

interface Props {
  brandId: number;
  isComplete?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isComplete: false
});

function handleRefresh() {
  window.location.reload();
}

const steps = [
  t('analysis_step_1', 'Fetching Pages'),
  t('analysis_step_2', 'Processing Content'),
  t('analysis_step_3', 'Generating Insights')
];

const currentStep = ref(0);
let statusInterval: ReturnType<typeof setInterval> | null = null;

const activities = [
  t('analysis_activity_fetching', 'Fetching website pages...'),
  t('analysis_activity_processing', 'Processing page content...'),
  t('analysis_activity_prompts', 'Running AI prompts...'),
  t('analysis_activity_comparing', 'Comparing LLM outputs...'),
  t('analysis_activity_finalizing', 'Finalizing results...')
];

let activityIndex = 0;
const MAX_ACTIVITY_INDEX = activities.length - 1;

onMounted(() => {
  statusInterval = setInterval(() => {
    if (activityIndex >= MAX_ACTIVITY_INDEX) {
      return
    }
    
    activityIndex++
    
    if (activityIndex === 1) currentStep.value = 0;
    if (activityIndex === 2) currentStep.value = 1;
    if (activityIndex >= 3) currentStep.value = 2;
  }, 3000);
});

onUnmounted(() => {
  if (statusInterval) {
    clearInterval(statusInterval);
  }
});
</script>

<style scoped>
.analysis-running-wall {
  background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
}

.loading-container {
  animation: fadeIn 0.5s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
